import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tradesperson } from '@/lib/types';
import { getPostcodeTradespeople, getPopularTradespeopleByCountry, addUserTradesperson, getTradesPeopleByPostcodeWithCounts } from '@/lib/supabase/trades';
import { toast } from 'sonner';
import { Loader2, CheckCircle, Users, MapPin, Sparkles } from 'lucide-react';
import { getTradeIconForTrade, getTradeDisplayName, getTradeType } from '@/lib/tradeUtils';
import { TradeIcon } from '@/components/ui/trade-icon';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { debug } from '@/lib/utils';
import { trackEvent } from '@/lib/analytics';
import { TradePersonCard } from '../TradePersonCard';

type SelectedTradesState = Record<string, boolean>;

interface TradePersonSelectionStepProps {
  postcode: string;
  onComplete: () => void;
  onSkip: () => void;
}

export function TradePersonSelectionStep({ postcode, onComplete, onSkip }: TradePersonSelectionStepProps) {
  const { user } = useAuth();
  const [tradespeople, setTradespeople] = useState<Tradesperson[]>([]);
  const [selectedTrades, setSelectedTrades] = useState<SelectedTradesState>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [filteredTradespeople, setFilteredTradespeople] = useState<Tradesperson[]>([]);
  const [showingFallback, setShowingFallback] = useState(false);

  // Calculate if any tradespeople are selected
  const hasSelections = Object.values(selectedTrades).some(selected => selected);

  // Load saved selections from session storage
  useEffect(() => {
    const sessionKey = `tp_selection_${postcode}`;
    const savedSelections = sessionStorage.getItem(sessionKey);

    if (savedSelections) {
      try {
        const parsed = JSON.parse(savedSelections);
        setSelectedTrades(parsed);
      } catch (error) {
        debug.error('Error parsing saved selections:', error);
      }
    }
  }, [postcode]);

  // Save selections to session storage when they change
  useEffect(() => {
    if (Object.keys(selectedTrades).length > 0) {
      const sessionKey = `tp_selection_${postcode}`;
      sessionStorage.setItem(sessionKey, JSON.stringify(selectedTrades));
    }
  }, [selectedTrades, postcode]);

  // Load tradespeople for the user's postcode
  useEffect(() => {
    async function loadTradespeople() {
      setIsLoading(true);

      try {
        // Try to get tradespeople by postcode first with recommendation counts
        const trades = await getTradesPeopleByPostcodeWithCounts(postcode, 30);

        // Check if we're showing fallback results (2-letter prefix matches)
        const isFallback = trades.length > 0 && trades.every(trade => {
          const hasPostcode = trade.postcode && Array.isArray(trade.postcode) && trade.postcode.length > 0;
          return hasPostcode && !trade.postcode.some(pc => pc.toUpperCase().startsWith(postcode.toUpperCase()));
        });

        setShowingFallback(isFallback);

        // No fallback to popular tradespeople - only show local ones
        // This ensures users only see tradespeople from their actual area

        // Sort by save count (descending) so most popular appear first
        trades.sort((a, b) => {
          const aCount = Number(a.save_count || 0);
          const bCount = Number(b.save_count || 0);
          return bCount - aCount;
        });

        // Extract unique categories
        const uniqueCategories = Array.from(
          new Set(trades.map(trade => getTradeType(trade)).filter(Boolean))
        );

        setTradespeople(trades);
        setFilteredTradespeople(trades);
        setCategories(uniqueCategories);

        // If no tradespeople found at all, auto-navigate to logged-in page
        if (trades.length === 0) {
          debug.log('No tradespeople found for postcode', postcode, '- redirecting to logged-in page');
          toast.info('No tradespeople found in your area yet. You can add them manually.');

          // Short delay to let user see the message
          setTimeout(() => {
            onComplete();
          }, 2000);
        }
      } catch (error) {
        debug.error('Error loading tradespeople for selection:', error);
        toast.error('Failed to load recommended tradespeople');
      } finally {
        setIsLoading(false);
      }
    }

    loadTradespeople();
  }, [postcode, onComplete]);

  // Filter tradespeople by category
  useEffect(() => {
    if (!activeCategory) {
      setFilteredTradespeople(tradespeople);
    } else {
      const filtered = tradespeople.filter(
        trade => getTradeType(trade) === activeCategory
      );
      setFilteredTradespeople(filtered);
    }
  }, [activeCategory, tradespeople]);

  // Toggle selection for a tradesperson
  const toggleSelection = (id: string) => {
    const newState = !selectedTrades[id];
    setSelectedTrades(prev => ({
      ...prev,
      [id]: newState
    }));

    // Track selection events
    const tradeperson = tradespeople.find(t => t.id === id);
    if (tradeperson) {
      trackEvent(newState ? 'tradesperson_selected' : 'tradesperson_deselected', {
        trade_type: getTradeType(tradeperson),
        postcode: postcode,
        has_phone: !!tradeperson.phone,
        has_email: !!tradeperson.email,
        verified: !!tradeperson.verified,
      });
    }
  };

  // Select a category filter
  const selectCategory = (category: string | null) => {
    const newCategory = activeCategory === category ? null : category;
    setActiveCategory(newCategory);

    // Track category filter usage
    trackEvent('tradesperson_category_filter', {
      category: category,
      action: newCategory ? 'apply' : 'clear',
      postcode: postcode,
    });
  };

  // Select/deselect all tradespeople in a category
  const toggleCategorySelection = (category: string) => {
    // Find all tradespeople in this category
    const tradesInCategory = tradespeople.filter(
      trade => getTradeType(trade) === category
    );

    // Check if all are currently selected
    const allSelected = tradesInCategory.every(trade => selectedTrades[trade.id]);
    const newSelectionState = !allSelected;

    // Toggle selection based on current state
    const newSelections = { ...selectedTrades };

    tradesInCategory.forEach(trade => {
      newSelections[trade.id] = newSelectionState;
    });

    setSelectedTrades(newSelections);

    // Track bulk selection
    trackEvent('tradesperson_bulk_selection', {
      category: category,
      action: newSelectionState ? 'select_all' : 'deselect_all',
      count: tradesInCategory.length,
      postcode: postcode,
    });
  };

  // Save selected tradespeople to user's account
  const saveSelectedTradespeople = async () => {
    if (!user) {
      toast.error('You must be logged in to save tradespeople');
      return;
    }

    const selectedIds = Object.entries(selectedTrades)
      .filter(([_, selected]) => selected)
      .map(([id]) => id);

    if (selectedIds.length === 0) {
      onComplete();
      return;
    }

    setIsSaving(true);

    try {
      // Add each selected tradesperson to the user's list
      const promises = selectedIds.map(id =>
        addUserTradesperson(user.id, id, {
          notes: `Added during signup from ${postcode.toUpperCase()}`,
          preferred: false
        })
      );

      await Promise.all(promises);

      // Clear the session storage after successful save
      const sessionKey = `tp_selection_${postcode}`;
      sessionStorage.removeItem(sessionKey);

      // Track successful save with analytics
      trackEvent('tradesperson_selection_saved', {
        count: selectedIds.length,
        postcode: postcode,
        categories: Array.from(new Set(
          selectedIds.map(id => {
            const trade = tradespeople.find(t => t.id === id);
            return trade ? getTradeType(trade) : 'unknown';
          })
        )).join(','),
      });

      toast.success(`${selectedIds.length} tradespeople added to your list!`);
      onComplete();
    } catch (error) {
      debug.error('Error saving selected tradespeople:', error);
      toast.error('Failed to save some tradespeople');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="w-full border-2 border-purple-900 rounded-xl shadow-lg">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center" style={{ color: "#5D4A9C" }}>
          Add Local Tradespeople
        </CardTitle>
        <CardDescription className="text-center text-purple-700">
          {showingFallback
            ? `No exact matches for ${postcode.toUpperCase()}, showing results from ${postcode.substring(0, 2).toUpperCase()} area`
            : "Select trusted tradespeople in your area to add to your list"
          }
        </CardDescription>
      </CardHeader>

      <CardContent>
        {/* Category filter - matching TradesList design */}
        {categories.length > 0 && (
          <div className="mb-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-medium text-purple-800">Filter by Trade Type</h3>
              {activeCategory && (
                <button
                  onClick={() => toggleCategorySelection(activeCategory)}
                  className="text-xs text-purple-700 hover:text-purple-900 underline"
                >
                  {filteredTradespeople.every(trade => selectedTrades[trade.id])
                    ? "Deselect All"
                    : "Select All"}
                </button>
              )}
            </div>
            <div className="relative">
              <div className="overflow-x-auto no-scrollbar" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                <div className="flex items-center py-2 px-1 min-w-full">
                  {categories.map(category => {
                    const isActive = activeCategory === category;

                    // Count how many tradespeople in this category are selected
                    const categoryTrades = tradespeople.filter(t => getTradeType(t) === category);
                    const selectedCount = categoryTrades.filter(t => selectedTrades[t.id]).length;
                    const hasSelections = selectedCount > 0;

                    // Get total count of tradespeople in this category
                    const totalCount = categoryTrades.length;

                    return (
                      <div key={category} className="relative flex-shrink-0 mr-4">
                        <button
                          onClick={() => selectCategory(category)}
                          title={isActive ? `${category} (tap again to clear)` : category}
                          aria-label={isActive ? `Clear ${category} filter` : `Filter by ${category}`}
                          className={`flex relative items-center justify-center rounded-full w-14 h-14 transition-colors ${
                            isActive
                              ? 'bg-purple-700 shadow-md'
                              : 'bg-purple-100 hover:bg-purple-200'
                          }`}
                        >
                          <TradeIcon
                            tradeType={category}
                            className="h-5 w-5"
                            categoryButton={true}
                            isActive={isActive}
                          />
                        </button>
                        {/* Count badge below circle */}
                        <div className={`text-center mt-1 text-xs font-medium ${
                          isActive ? 'text-purple-700' : 'text-gray-600'
                        }`}>
                          {totalCount}
                        </div>
                        {/* Selection count indicator */}
                        {hasSelections && (
                          <div className="absolute -top-1 -right-1 bg-purple-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center shadow-sm">
                            {selectedCount}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading state */}
        {isLoading ? (
          <div className="py-8 flex justify-center items-center">
            <Loader2 className="h-8 w-8 text-purple-700 animate-spin" />
            <p className="ml-3 text-purple-700">Finding tradespeople in your area...</p>
          </div>
        ) : filteredTradespeople.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-lg text-purple-700 mb-2">No tradespeople found</p>
            <p className="text-sm text-purple-600">
              {activeCategory
                ? `No ${activeCategory} found in your area. Try another category or continue to add manually later.`
                : tradespeople.length === 0
                  ? `We couldn't find tradespeople in your area yet. Redirecting you to your dashboard...`
                  : `We couldn't find tradespeople in your area yet. You can add them manually later.`
              }
            </p>
          </div>
        ) : (
          <div className="space-y-3 mb-5 max-h-[400px] overflow-y-auto p-1">
            {filteredTradespeople.map(trade => (
              <SelectionTradePersonCard
                key={trade.id}
                trade={trade}
                isSelected={!!selectedTrades[trade.id]}
                onToggle={() => toggleSelection(trade.id)}
                postcode={postcode}
              />
            ))}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col space-y-3 mt-8">
          <Button
            onClick={saveSelectedTradespeople}
            disabled={isSaving || (!hasSelections && !isLoading)}
            className="w-full bg-purple-700 hover:bg-purple-800 py-2.5 text-base font-medium"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving selections...
              </>
            ) : hasSelections ? (
              <>
                <CheckCircle className="mr-2 h-5 w-5" />
                Add {Object.values(selectedTrades).filter(Boolean).length} selected
              </>
            ) : (
              'Continue'
            )}
          </Button>

          <Button
            onClick={() => {
              // Clear session storage when skipping
              const sessionKey = `tp_selection_${postcode}`;
              sessionStorage.removeItem(sessionKey);

              // Track skip event
              trackEvent('tradesperson_selection_skipped', {
                postcode: postcode,
                available_tradespeople: tradespeople.length,
                filtered_count: filteredTradespeople.length,
                had_selections: Object.values(selectedTrades).some(Boolean),
              });

              onSkip();
            }}
            variant="outline"
            className="w-full border-gray-300 text-gray-700 hover:bg-gray-100"
            disabled={isSaving}
          >
            Skip for now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Selection card wrapper component
interface SelectionTradePersonCardProps {
  trade: Tradesperson;
  isSelected: boolean;
  onToggle: () => void;
  postcode: string;
}

function SelectionTradePersonCard({ trade, isSelected, onToggle, postcode }: SelectionTradePersonCardProps) {
  const displayName = getTradeDisplayName(trade);
  const tradeType = getTradeType(trade);

  return (
    <div
      onClick={onToggle}
      className={`p-3 border-2 rounded-xl transition-all cursor-pointer relative ${
        isSelected
          ? 'border-purple-500 bg-purple-50'
          : 'border-purple-300 bg-white hover:bg-purple-50'
      }`}
    >
      {/* Badge Container - Absolute Positioned */}
      <div className="absolute top-0 right-0 z-10 flex flex-col items-end gap-1">
        {/* Popular Badge */}
        {Number(trade.save_count || 0) >= 10 && (
          <Badge className="bg-purple-100 text-purple-700 border-0 rounded-full px-1.5 py-0.5 text-xs font-medium animate-fade-scale-in">
            <Sparkles className="h-2.5 w-2.5 mr-1" /> Popular
          </Badge>
        )}

        {/* Save count badge */}
        {Number(trade.save_count || 0) > 0 && Number(trade.save_count || 0) < 10 && (
          <Badge className="bg-blue-100 text-blue-700 border-0 rounded-full px-1.5 py-0.5 text-xs font-medium animate-fade-scale-in">
            <Users className="h-2.5 w-2.5 mr-1" /> {trade.save_count} saved
          </Badge>
        )}

        {/* Verified Badge */}
        {trade.verified && (
          <Badge variant="verified" className="border-0 rounded-full px-1.5 py-0.5 text-xs font-medium animate-fade-scale-in">
            <CheckCircle className="h-2.5 w-2.5 mr-1 stroke-[3]" /> Verified
          </Badge>
        )}
      </div>

      {/* Main Card Content */}
      <div className="flex items-start mb-1 pr-24"> {/* Added right padding for badges */}
        <div className="flex items-start">
          <div className="flex-shrink-0 mr-3">
            <TradeIcon tradesperson={trade} className="h-16 w-16" />
          </div>
          <div>
            <h3 className="font-bold text-lg text-purple-900 leading-tight">
              {displayName}
            </h3>
            <div className="flex items-center mt-0.5">
              <p className="text-sm text-purple-600">{tradeType}</p>
            </div>

            {/* Location info */}
            {trade.postcode && trade.postcode.length > 0 && (
              <div className="flex items-center mt-2 text-sm text-gray-500">
                <MapPin className="h-4 w-4 mr-1" />
                {trade.postcode[0].toUpperCase()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Selection indicator */}
      <div className="absolute bottom-3 right-3">
        <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center transition-colors ${
          isSelected
            ? 'border-purple-500 bg-purple-500'
            : 'border-gray-300'
        }`}>
          {isSelected && <CheckCircle className="h-4 w-4 text-white" />}
        </div>
      </div>
    </div>
  );
}
